import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Row, Col, Input, Select, Button } from 'antd';
import { SearchOutlined, FilterOutlined } from '@ant-design/icons';

const { Option } = Select;

interface CSAFiltersProps {
  onSearch: (value: string) => void;
  onFilterChange: (key: string, value: any) => void;
  onToggleAdvancedFilters: () => void;
  showAdvancedFilters: boolean;
  loading?: boolean;
  activeSeverity?: string;
  activeHost?: string;
  activeSearch?: string;
}

const CSAFilters: React.FC<CSAFiltersProps> = ({
  onSearch,
  onFilterChange,
  onToggleAdvancedFilters,
  showAdvancedFilters,
  loading = false,
  activeSeverity,
  activeHost,
  activeSearch
}) => {
  // Estados locais para inputs com debounce
  const [hostInputValue, setHostInputValue] = useState(activeHost || '');
  const [cveInputValue, setCveInputValue] = useState('');
  const [ipInputValue, setIpInputValue] = useState('');
  const [portInputValue, setPortInputValue] = useState('');

  // Refs para timers de debounce
  const hostTimerRef = useRef<NodeJS.Timeout | null>(null);
  const cveTimerRef = useRef<NodeJS.Timeout | null>(null);
  const ipTimerRef = useRef<NodeJS.Timeout | null>(null);
  const portTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Função de debounce estável
  const debounce = useCallback((
    callback: () => void,
    delay: number,
    timerRef: React.MutableRefObject<NodeJS.Timeout | null>
  ) => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(callback, delay);
  }, []);

  // Sincronizar apenas quando activeHost mudar externamente
  useEffect(() => {
    setHostInputValue(activeHost || '');
  }, [activeHost]);

  // Cleanup timers
  useEffect(() => {
    return () => {
      if (hostTimerRef.current) clearTimeout(hostTimerRef.current);
      if (cveTimerRef.current) clearTimeout(cveTimerRef.current);
      if (ipTimerRef.current) clearTimeout(ipTimerRef.current);
      if (portTimerRef.current) clearTimeout(portTimerRef.current);
    };
  }, []);

  // Handlers com debounce
  const handleHostChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setHostInputValue(value);
    
    debounce(() => {
      onFilterChange('host', value.trim() || undefined);
    }, 500, hostTimerRef);
  }, [onFilterChange, debounce]);

  const handleCveChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCveInputValue(value);
    
    debounce(() => {
      onFilterChange('cve', value.trim() || undefined);
    }, 500, cveTimerRef);
  }, [onFilterChange, debounce]);

  const handleIpChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setIpInputValue(value);
    
    debounce(() => {
      onFilterChange('ip', value.trim() || undefined);
    }, 500, ipTimerRef);
  }, [onFilterChange, debounce]);

  const handlePortChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPortInputValue(value);
    
    debounce(() => {
      onFilterChange('port', value.trim() || undefined);
    }, 500, portTimerRef);
  }, [onFilterChange, debounce]);

  // Clear handlers
  const handleHostClear = useCallback(() => {
    setHostInputValue('');
    onFilterChange('host', undefined);
  }, [onFilterChange]);

  const handleCveClear = useCallback(() => {
    setCveInputValue('');
    onFilterChange('cve', undefined);
  }, [onFilterChange]);

  const handleIpClear = useCallback(() => {
    setIpInputValue('');
    onFilterChange('ip', undefined);
  }, [onFilterChange]);

  const handlePortClear = useCallback(() => {
    setPortInputValue('');
    onFilterChange('port', undefined);
  }, [onFilterChange]);

  return (
    <div style={{ marginBottom: 16 }}>
      <Row gutter={[8, 8]} align="middle">
        <Col xs={24} sm={8} md={6}>
          <Input.Search
            placeholder="Buscar vulnerabilidades..."
            onSearch={onSearch}
            allowClear
            enterButton={<SearchOutlined />}
            disabled={loading}
            defaultValue={activeSearch}
          />
        </Col>
        <Col xs={24} sm={6} md={4}>
          <Select
            placeholder="Severidade"
            allowClear
            style={{ width: '100%' }}
            value={activeSeverity}
            onChange={(value) => onFilterChange('severity', value)}
            disabled={loading}
          >
            <Option value="high">Alta</Option>
            <Option value="medium">Média</Option>
            <Option value="low">Baixa</Option>
            <Option value="info">Informativa</Option>
          </Select>
        </Col>
        <Col xs={24} sm={6} md={4}>
          <Input
            placeholder="Filtrar por host"
            value={hostInputValue}
            onChange={handleHostChange}
            disabled={loading}
            suffix={
              hostInputValue ? (
                <Button
                  type="text"
                  size="small"
                  onClick={handleHostClear}
                  style={{ 
                    padding: 0, 
                    minWidth: 'auto',
                    height: 'auto',
                    lineHeight: 1
                  }}
                >
                  ✕
                </Button>
              ) : null
            }
          />
        </Col>
        <Col xs={24} sm={4} md={2}>
          <Button
            icon={<FilterOutlined />}
            onClick={onToggleAdvancedFilters}
            type={showAdvancedFilters ? 'primary' : 'default'}
            disabled={loading}
          >
            Filtros
          </Button>
        </Col>
      </Row>
      
      {/* Filtros avançados */}
      {showAdvancedFilters && (
        <div style={{ 
          marginTop: 16, 
          padding: 16, 
          background: '#fafafa', 
          borderRadius: 6,
          border: '1px solid #d9d9d9'
        }}>
          <Row gutter={[8, 8]}>
            <Col xs={24} sm={8} md={6}>
              <Input
                placeholder="Filtrar por CVE"
                value={cveInputValue}
                onChange={handleCveChange}
                disabled={loading}
                suffix={
                  cveInputValue ? (
                    <Button
                      type="text"
                      size="small"
                      onClick={handleCveClear}
                      style={{ 
                        padding: 0, 
                        minWidth: 'auto',
                        height: 'auto',
                        lineHeight: 1
                      }}
                    >
                      ✕
                    </Button>
                  ) : null
                }
              />
            </Col>
            <Col xs={24} sm={8} md={6}>
              <Input
                placeholder="Filtrar por IP"
                value={ipInputValue}
                onChange={handleIpChange}
                disabled={loading}
                suffix={
                  ipInputValue ? (
                    <Button
                      type="text"
                      size="small"
                      onClick={handleIpClear}
                      style={{ 
                        padding: 0, 
                        minWidth: 'auto',
                        height: 'auto',
                        lineHeight: 1
                      }}
                    >
                      ✕
                    </Button>
                  ) : null
                }
              />
            </Col>
            <Col xs={24} sm={8} md={6}>
              <Input
                placeholder="Filtrar por porta"
                value={portInputValue}
                onChange={handlePortChange}
                disabled={loading}
                suffix={
                  portInputValue ? (
                    <Button
                      type="text"
                      size="small"
                      onClick={handlePortClear}
                      style={{ 
                        padding: 0, 
                        minWidth: 'auto',
                        height: 'auto',
                        lineHeight: 1
                      }}
                    >
                      ✕
                    </Button>
                  ) : null
                }
              />
            </Col>
            <Col xs={24} sm={8} md={6}>
              <Select
                placeholder="Score CVSS mínimo"
                allowClear
                style={{ width: '100%' }}
                onChange={(value) => onFilterChange('minCvssScore', value)}
                disabled={loading}
              >
                <Option value={0}>0.0+</Option>
                <Option value={4}>4.0+ (Médio)</Option>
                <Option value={7}>7.0+ (Alto)</Option>
              </Select>
            </Col>
          </Row>
        </div>
      )}
    </div>
  );
};

export default CSAFilters;